import {
  useEffect,
  useId,
  useRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';

/**
 * The FirstLight component vocabulary (ADR-0007).
 *
 * Square corners throughout, no shadows, and status carried by a word as well
 * as a colour — gold and melon are fills and borders only, never type.
 */

export type Tone = 'neutral' | 'positive' | 'attention' | 'urgent' | 'info';

// ---------------------------------------------------------------- page
export function Page({ children }: { children: ReactNode }) {
  return <div className="page">{children}</div>;
}

export function PageHead({
  title,
  subtitle,
  actions,
  breadcrumb,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
}) {
  return (
    <header className="page__head">
      <div className="page__title">
        {breadcrumb}
        <h1>{title}</h1>
        {subtitle ? <p className="page__subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="page__actions">{actions}</div> : null}
    </header>
  );
}

export function Card({
  children,
  variant,
  className = '',
}: {
  children: ReactNode;
  variant?: 'urgent' | 'attention' | 'positive' | 'flush' | 'raised';
  className?: string;
}) {
  return (
    <section className={`card${variant ? ` card--${variant}` : ''} ${className}`.trim()}>
      {children}
    </section>
  );
}

export function CardHead({ title, actions }: { title: ReactNode; actions?: ReactNode }) {
  return (
    <div className="card__head">
      {typeof title === 'string' ? <h3>{title}</h3> : title}
      {actions}
    </div>
  );
}

export function Eyebrow({ children }: { children: string }) {
  return <span className="eyebrow">{children}</span>;
}

export function Caption({ children }: { children: ReactNode }) {
  return <p className="caption">{children}</p>;
}

// ---------------------------------------------------------------- status
export function Tag({ label, tone = 'neutral' }: { label: string; tone?: Tone }) {
  // The label carries the meaning; the tint only reinforces it.
  return (
    <span className="tag" data-tone={tone}>
      {label}
    </span>
  );
}

export function Notice({
  title,
  children,
  tone = 'positive',
}: {
  title?: string;
  children?: ReactNode;
  tone?: Tone;
}) {
  return (
    <div className="notice" data-tone={tone} role={tone === 'urgent' ? 'alert' : undefined}>
      {title ? <div className="notice__title">{title}</div> : null}
      {children ? <div className="notice__body">{children}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- counts
export function CountCard({
  label,
  value,
  context,
  tone = 'neutral',
  onClick,
}: {
  label: string;
  value: number | string;
  context?: string;
  tone?: 'neutral' | 'urgent' | 'attention';
  onClick?: () => void;
}) {
  // Every count opens its list — a number a supervisor cannot act on is
  // decoration.
  return (
    <button type="button" className="count" data-tone={tone} onClick={onClick}>
      <span className="eyebrow">{label}</span>
      <span className="count__value">{value}</span>
      {context ? <span className="count__context">{context}</span> : null}
    </button>
  );
}

// ---------------------------------------------------------------- identity
export type Person = {
  id: string;
  name: string;
  initials: string;
  color: string;
  is_primary?: boolean;
};

export function Avatar({
  person,
  size,
}: {
  person: Pick<Person, 'name' | 'initials' | 'color'>;
  size?: 'sm' | 'lg' | 'xl';
}) {
  return (
    <span
      className="avatar"
      data-size={size}
      style={{ background: person.color }}
      title={person.name}
      aria-hidden="true"
    >
      {person.initials}
    </span>
  );
}

export function AvatarStack({ people }: { people: Person[] }) {
  // Coverage depth reads at a glance: three faces is a team, one is a risk.
  return (
    <span className="avatar-stack" aria-label={people.map((p) => p.name).join(', ')}>
      {people.map((person) => (
        <Avatar key={person.id} person={person} size="sm" />
      ))}
    </span>
  );
}

// ---------------------------------------------------------------- rates
export function RateBar({
  label,
  percent,
  numerator,
  denominator,
  target,
  meetsTarget,
}: {
  label: string;
  percent: number | null;
  numerator: number;
  denominator: number;
  target: number;
  meetsTarget: boolean;
}) {
  return (
    <div className="rate">
      <div className="rate__head">
        <span>{label}</span>
        <span className="rate__value">{percent === null ? 'No data' : `${percent}%`}</span>
      </div>
      <div
        className="rate__track"
        role="img"
        aria-label={
          percent === null
            ? `${label}: no data yet`
            : `${label}: ${percent} percent of ${denominator}, against a target of ${target} percent`
        }
      >
        <div className="rate__fill" style={{ width: `${percent ?? 0}%` }} data-below={!meetsTarget} />
        {/* The target is a mark on the track, so a rate is always read against
            something rather than in isolation. */}
        <div className="rate__target" style={{ left: `${target}%` }} />
      </div>
      <span className="caption">
        {numerator} of {denominator} · agency target {target}%
      </span>
    </div>
  );
}

export function ValueBar({
  label,
  score,
  suppressed,
  isLowest,
  responses,
}: {
  label: string;
  score: number | null;
  suppressed: boolean;
  isLowest?: boolean;
  responses: number;
}) {
  return (
    <div className="value-bar">
      <span>{label}</span>
      <span className="value-bar__track">
        {!suppressed && score !== null ? (
          <span
            className="value-bar__fill"
            style={{ width: `${(score / 5) * 100}%` }}
            data-low={isLowest}
          />
        ) : null}
      </span>
      <span className="value-bar__score">
        {suppressed || score === null ? (
          <span title={`${responses} responses — below the display threshold`}>—</span>
        ) : (
          score.toFixed(1)
        )}
      </span>
    </div>
  );
}

/** A score, or the words that replace it below the threshold. */
export function Score({
  score,
  suppressed,
  label,
  responses,
}: {
  score: number | null;
  suppressed: boolean;
  label: string;
  responses: number;
}) {
  if (suppressed || score === null) {
    return <span className="caption">{label}</span>;
  }
  return (
    <span>
      <span className="numeric numeric--sm">{score.toFixed(1)}</span>{' '}
      <span className="caption">{responses} responses</span>
    </span>
  );
}

// ---------------------------------------------------------------- controls
export function Button({
  children,
  onClick,
  variant = 'primary',
  size,
  disabled,
  busy,
  type = 'button',
  block,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'urgent' | 'ghost';
  size?: 'lg';
  disabled?: boolean;
  busy?: boolean;
  type?: 'button' | 'submit';
  block?: boolean;
}) {
  const classes = [
    'btn',
    variant !== 'primary' ? `btn--${variant}` : '',
    size === 'lg' ? 'btn--lg' : '',
    block ? 'btn--block' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
    >
      {busy ? 'Working…' : children}
    </button>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: (props: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean;
  }) => ReactNode;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {hint ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {children({
        id,
        'aria-describedby': [hintId, errorId].filter(Boolean).join(' ') || undefined,
        'aria-invalid': Boolean(error),
      })}
      {error ? (
        <span className="field__error" id={errorId}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement> & { id: string }) {
  return <input className="input" {...props} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement> & { id: string }) {
  return <textarea className="textarea" {...props} />;
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (next: T) => void;
  label: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
  label,
}: {
  tabs: { value: T; label: string; count?: number }[];
  active: T;
  onChange: (next: T) => void;
  label: string;
}) {
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab.value}
          role="tab"
          type="button"
          className="tab"
          aria-selected={tab.value === active}
          onClick={() => onChange(tab.value)}
        >
          {tab.label}
          {tab.count !== undefined ? ` (${tab.count})` : ''}
        </button>
      ))}
    </div>
  );
}

export function PolicySwitch({
  on,
  label,
  why,
  locked,
}: {
  on: boolean;
  label: string;
  why?: string;
  locked?: boolean;
}) {
  return (
    <div className="switch-row">
      <span className="switch-state" data-on={on}>
        {on ? 'On' : 'Off'}
      </span>
      <span>
        {label}
        {locked ? <span className="caption"> · always on</span> : null}
        {why ? <div className="caption">{why}</div> : null}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------- data
export function Table({
  columns,
  children,
  empty,
  widths,
}: {
  columns: string[];
  children: ReactNode;
  empty?: string;
  widths?: string[];
}) {
  const hasRows = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return (
    <div className="card card--flush">
      <table className="table">
        {widths ? (
          <colgroup>
            {widths.map((width, index) => (
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
        ) : null}
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column} scope="col">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {hasRows ? (
            children
          ) : (
            <tr>
              <td colSpan={columns.length}>
                <div className="table__empty">{empty ?? 'Nothing here.'}</div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <div className="spinner" aria-hidden="true" />
      {label}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <Card variant="urgent">
      <div className="stack stack--tight">
        <strong>Something went wrong</strong>
        <p>{message}</p>
        {onRetry ? (
          <div>
            <Button variant="secondary" onClick={onRetry}>
              Try again
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- modal
export function Modal({
  title,
  children,
  onClose,
  wide,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus();
    };
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={`modal${wide ? ' modal--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
      >
        <h2 style={{ fontSize: 21, marginBottom: 'var(--s-12)' }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}
